'use client'
import React, { useState, useEffect, useRef } from 'react'
import { useRouter, useSearchParams, usePathname } from 'next/navigation'
import LargePropertyCard from '@components/LargePropertyCard'
import dynamic from 'next/dynamic'
const SearchMap = dynamic(() => import('@components/SearchMap'), { ssr: false })
import MobileSearchMap from '@components/MobileSearchMap'
import MobileMapPropertyCard from '@components/MobileMapPropertyCard'
import { getFilteredProperties2 } from '@services/getFilteredProperties2'
import { getPropertyCardInfo } from '@services/getPropertyCardInfo'
import LoadingCard from '@components/LoadingCard'
import { saleTypes } from '@constants/saleTypes'

import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/navigation'

// ─── Icons ────────────────────────────────────────────────────────────────────

const GridIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
    <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
  </svg>
)
const MapIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
    <line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/>
  </svg>
)

// ─── CSS var resolver ─────────────────────────────────────────────────────────

const VAR_MAP = {
  'var(--color-main)':      '#019da4',
  'var(--color-secondary)': '#6b7280',
  'var(--color-price)':     '#019da4',
  'var(--thirdcolor)':      '#228be6',
  'var(--fourthcolor)':     '#d4d7d9',
  '#ffffff': '#ffffff',
  '#000000': '#000000',
}
const rv = (val, fb = '#228be6') => {
  if (!val) return fb
  if (val.startsWith?.('#') || val.startsWith?.('rgb')) return val
  return VAR_MAP[val] ?? fb
}

// ─── Main component ───────────────────────────────────────────────────────────
//
//  layoutMode (from searchPageConfig):
//    'standard'  – full-page property grid. Map toggle switches the whole
//                  view between grid and map+sidebar.
//    'rightmove' – fixed-height scrollable panel of cards. The map toggle
//                  works exactly the same as standard — it replaces the card
//                  panel with the map+sidebar view. No permanent split.
//
//  New props:
//    onPropertyCount(count) – lets parent pass count to PropertySearch bar
//

// ─── MapSidebarView ─────────────────────────────────────────────────────────
// Defined OUTSIDE SearchResultCards so React never unmounts it on re-render.
// This prevents the sidebar scroll position from resetting when hoveredProperty
// state changes in the parent.
const MapSidebarView = React.memo(function MapSidebarView({
    properties, isLoading, mapColsDesktop, sidebarStyle, mapPanelStyle,
    swiperRef, onMouseEnter, onMouseLeave, onSlideChange, onMarkerClick,
    newLimit, LoadMoreButton, PaginationControls,
    cardStyle, mapCardStyle, lang, dictionary, searchParams,
    coordinates, hoveredPropertyRef, activeProperty,
    activePropertyLat, activePropertyLng, mapPassthrough,
}) {
    return (

        <div className='w-full relative flex flex-row mx-auto'>
            {/* Desktop sidebar */}
            <div className='md:flex hidden md:h-[80vh] h-full p-3 overflow-hidden overflow-y-scroll flex-col items-center scrollable'
                style={sidebarStyle}>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${mapColsDesktop}, 1fr)`, gap: 8, width: '100%' }}>
                    {properties.map((property, index) => (
                        <div key={property.id} id={property.id}
                            onMouseEnter={() => onMouseEnter(property.id)}
                            onMouseLeave={onMouseLeave}
                            className='mb-2'>
                            <LargePropertyCard lang={lang} dictionary={dictionary.card} property={property}
                                isLast={index === properties.length - 1} newLimit={newLimit} cardStyle={cardStyle} />
                        </div>
                    ))}
                    {isLoading && Array.from({ length: mapColsDesktop }).map((_, i) => (
                        <LoadingCard key={`sk-${i}`} cardStyle={cardStyle} />
                    ))}
                </div>
                <LoadMoreButton />
                <PaginationControls />
            </div>

            {/* Mobile swiper */}
            <div className='md:hidden block absolute bottom-0 left-0 right-0 z-[52]' style={{ pointerEvents: 'none' }}>
                <div className='sm:hidden block' style={{ pointerEvents: 'auto' }}>
                    <Swiper slidesPerView={1} onSwiper={s => { swiperRef.current = s }} onSlideChange={onSlideChange}>
                        {properties.map((property, index) => (
                            <SwiperSlide key={property.id} id={property.id}
                                data-latitude={Number(property.latitude)}
                                data-longitude={Number(property.longitude)}>
                                <MobileMapPropertyCard lang={lang} dictionary={dictionary.card} property={property}
                                    cardStyle={mapCardStyle} isLast={index === properties.length - 1} newLimit={newLimit} />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </div>

            {/* Desktop map */}
            <div className='md:block hidden md:h-[80vh] h-full md:relative absolute overflow-hidden'
                style={{ ...mapPanelStyle, zIndex: 1 }}>
                <SearchMap cardStyle={mapCardStyle} coordinates={coordinates} hoveredProperty={hoveredPropertyRef.current}
                    latitude={searchParams.latitude} longitude={searchParams.longitude}
                    lang={lang} dictionary={dictionary} mapConfig={mapPassthrough} />
            </div>

            {/* Mobile map */}
            <div className='md:hidden block w-full h-full z-[51]'>
                <MobileSearchMap coordinates={coordinates} hoveredProperty={activeProperty} zoomControl={false}
                    latitude={activePropertyLat} longitude={activePropertyLng}
                    lang={lang} dictionary={dictionary} mapConfig={mapPassthrough}
                    cardStyle={mapCardStyle} onMarkerClick={onMarkerClick} />
            </div>
        </div>
    )
})

const SearchResultCards = ({
    searchParams,
    cardStyle,
    mapCardStyle,
    dictionary,
    lang             = 'en',
    scrollMode       = 'pagination',
    searchPageConfig,
    onPropertyCount,
}) => {
    // ── Resolve config ─────────────────────────────────────────────────────────
    const cfg                = searchPageConfig ?? {}
    // In Rightmove mode the scroll mode lives at cfg.rightmove.scrollMode
    // Fall back to cfg.scrollMode, then the legacy prop
    const resolvedScrollMode = (cfg.layoutMode === 'rightmove'
        ? (cfg.rightmove?.scrollMode ?? cfg.scrollMode ?? scrollMode)
        : (cfg.scrollMode ?? scrollMode)
    )

    // Layout mode: 'standard' | 'rightmove'
    const layoutMode    = cfg.layoutMode ?? 'standard'
    const isRightmove   = layoutMode === 'rightmove'

    // Standard grid columns
    const colsDesktop  = cfg.colsDesktop ?? 4
    const colsTablet   = cfg.colsTablet  ?? 2
    const colsMobile   = cfg.colsMobile  ?? 1
    const cardGap      = cfg.cardGap     ?? 12

    // Which views are available (both modes respect these)
    const allowListView = cfg.allowListView !== false
    const allowMapView  = cfg.allowMapView  !== false
    const defaultView   = cfg.defaultView   ?? 'list'

    // Pagination / button styles
    const btnBg        = cfg.btnBg                ?? 'var(--thirdcolor)'
    const btnTextColor = cfg.btnTextColor         ?? '#ffffff'
    const btnRadius    = cfg.btnRadius            ?? 6
    const pActiveBg    = cfg.paginationActiveBg   ?? 'var(--thirdcolor)'
    const pActiveTc    = cfg.paginationActiveText ?? '#ffffff'
    const pBorderColor = cfg.paginationBorderColor ?? '#e5e7eb'

    // Rightmove-mode panel config (lives at cfg.rightmove.*)
    const rmCfg         = cfg.rightmove ?? {}
    const rmListCols    = rmCfg.listCols        ?? 1
    const rmCardGap     = rmCfg.cardGap         ?? 12
    const rmVh          = rmCfg.viewportHeight  ?? 82
    const rmShowCount   = rmCfg.showResultCount ?? true
    const rmCountPos    = rmCfg.resultCountPos  ?? 'top'
    const rmBorderColor = rmCfg.borderColor     ?? '#e5e7eb'
    const rmListBg      = rmCfg.listBg          ?? '#f9fafb'

    // Map layout (same config object for both modes)
    const mapCfg         = cfg.map ?? {}
    const splitRatio     = mapCfg.splitRatio     ?? 40
    const sidebarSide    = mapCfg.sidebarSide    ?? 'left'
    const mapColsDesktop = mapCfg.mapColsDesktop ?? 1

    // ── State ──────────────────────────────────────────────────────────────────
    let userEmail = null

    const router    = useRouter()
    const pathname  = usePathname()
    const urlParams = useSearchParams()

    const [isLoading,         setIsLoading]         = useState(false)
    const [page,              setPage]              = useState(
        resolvedScrollMode === 'pagination' ? (Number(urlParams.get('page')) || 1) : 1
    )
    const [allPages,          setAllPages]          = useState(1)
    const [properties,        setProperties]        = useState([])
    const [propertyCount,     setPropertyCount]     = useState(0)
    const [coordinates,       setCoordinates]       = useState([])
    const [hoveredProperty,   setHoveredProperty]   = useState(null)
    const [activeProperty,    setActiveProperty]    = useState({})
    const [activePropertyLat, setActivePropertyLat] = useState(mapCfg.defaultLat ?? 13.732772)
    const [activePropertyLng, setActivePropertyLng] = useState(mapCfg.defaultLng ?? 100.5801953)
    const [hasFetchedOnce, setHasFetchedOnce] = useState(false)

    // Map toggle — works identically in both layout modes
    const urlMapOn = searchParams?.map === 'true' || searchParams?.map === true
    const [showMap, setShowMap] = useState(() => {
        if (!allowMapView)  return false
        if (!allowListView) return true
        if (urlMapOn)       return true
        return defaultView === 'map'
    })

    const swiperRef          = useRef(null)
    const propertiesRef      = useRef([])
    // Ref so the memoized MapView always reads current hoveredProperty
    // without needing it in the deps array (which would remount the sidebar)
    const hoveredPropertyRef = useRef(null)

    // ── Map interaction handlers ───────────────────────────────────────────────
    const handleMouseEnter = (id) => { hoveredPropertyRef.current = id;  setHoveredProperty(id) }
    const handleMouseLeave = ()   => { hoveredPropertyRef.current = null; setHoveredProperty(null) }

    const handleSlideChange = (swiper) => {
        const idx   = swiper.activeIndex
        const slide = swiper.slides[idx]
        const lat   = Number(slide.dataset.latitude)
        const lng   = Number(slide.dataset.longitude)
        setActiveProperty(slide.id)
        if (isFinite(lat) && lat !== 0) setActivePropertyLat(lat)
        if (isFinite(lng) && lng !== 0) setActivePropertyLng(lng)
    }

    const handleMarkerClick = async ({ _id, latitude, longitude }) => {
        setActiveProperty(_id)
        setActivePropertyLat(Number(latitude))
        setActivePropertyLng(Number(longitude))

        const idx = propertiesRef.current.findIndex(p => p.id === _id || p._id === _id)
        if (idx !== -1) {
            swiperRef.current?.slideTo(idx)
            return
        }

        try {
            const fetched = await getPropertyCardInfo(_id)
            if (!fetched || fetched.message) return
            fetched.latitude  = latitude
            fetched.longitude = longitude
            const next = [fetched, ...propertiesRef.current]
            propertiesRef.current = next
            setProperties(next)
            setTimeout(() => swiperRef.current?.slideTo(0), 50)
        } catch(e) {
            console.error('getPropertyCardInfo failed', e)
        }
    }

    // ── Data fetching ──────────────────────────────────────────────────────────
    const getData = async (pageNumber, forceMap) => {
        setIsLoading(true)
        setHasFetchedOnce(true)
        const inMapMode = forceMap !== undefined ? forceMap : showMap

        const result = await getFilteredProperties2({
            saleType:     searchParams.saleType    || saleTypes[lang][0].value,
            name:         searchParams.name        || '',
            bts:          searchParams.bts         || '',
            district:     searchParams.district    || '',
            districts:    searchParams.districts   || '',
            province:     searchParams.province    || '',
            provinces:    searchParams.provinces   || '',
            location:     searchParams.location    || '',
            area:         searchParams.area        || '',
            project:      searchParams.project     || '',
            category:     searchParams.category    || '',
            categories:   searchParams.categories  || '',
            room:         searchParams.room        || '',
            minprice:     searchParams.minprice    || '',
            maxprice:     searchParams.maxprice    || '',
            minlivingarea:     searchParams.minlivingarea    || '',
            maxlivingarea:     searchParams.maxlivingarea    || '',
            petfriendly:  searchParams.petfriendly || '',
            seaview:      searchParams.seaview     || '',
            beachfront:   searchParams.beachfront  || '',
            agent:        searchParams.agent       || '',
            sort:         searchParams.sort        || 'datedesc',
            rooms:        searchParams.rooms        || '',
            bathroom:     searchParams.bathroom     || '',
            foreignquota: searchParams.foreignquota || '',
            mountainview: searchParams.mountainview || '',
            riverview:  searchParams.riverview || '',
            parkview:  searchParams.parkview || '',
            amenities:  searchParams.amenities || '',
            map:          inMapMode ? 'true' : (searchParams.map || false),
            page:         pageNumber,
        }, userEmail, lang)

        if (pageNumber > 1 && resolvedScrollMode !== 'pagination') {
            const next = [...propertiesRef.current, ...result.properties]
            propertiesRef.current = next
            setProperties(next)
        } else {
            propertiesRef.current = result.properties
            setProperties(result.properties)
        }

        setAllPages(result.pages)
        setCoordinates(result.coordinates ?? [])

        const count = result.count ?? 0
        setPropertyCount(count)
        onPropertyCount?.(count)

        if (result.properties.length > 0 && pageNumber === 1) {
            setActiveProperty(result.properties[0].id)
            setActivePropertyLat(Number(result.properties[0].latitude))
            setActivePropertyLng(Number(result.properties[0].longitude))
        }
        setIsLoading(false)
    }

    // Reset + fetch when search params change
    useEffect(() => {
        const urlPage = resolvedScrollMode === 'pagination'
            ? (Number(urlParams.get('page')) || 1)
            : 1
        setPage(urlPage)
        setProperties([])
        propertiesRef.current = []
        setHasFetchedOnce(false)
        if (urlMapOn && allowMapView)    setShowMap(true)
        else if (!urlMapOn && allowListView) setShowMap(false)
        getData(urlPage)
    }, [searchParams])

    // Fetch next page when `page` increments (infinite scroll / load more)
    // Skip page 1 — that's handled by the searchParams effect above
    useEffect(() => {
        if (page <= 1) return
        getData(page)
    }, [page])

    // ── View toggle — identical behaviour in both layout modes ─────────────────
    const handleViewToggle = (nextIsMap) => {
        if (nextIsMap && !allowMapView)   return
        if (!nextIsMap && !allowListView) return
        setShowMap(nextIsMap)
        const params = new URLSearchParams(urlParams.toString())
        if (nextIsMap) params.set('map', 'true')
        else params.delete('map')
        router.replace(`${pathname}?${params.toString()}`, { scroll: false })
        if (nextIsMap && coordinates.length === 0) getData(page, true)
    }

    // ── Pagination ─────────────────────────────────────────────────────────────
    const goToPage = (p) => {
        const next = Math.max(1, Math.min(allPages, p))
        setPage(next)
        const params = new URLSearchParams(urlParams.toString())
        if (next === 1) params.delete('page')
        else params.set('page', String(next))
        router.push(`${pathname}?${params.toString()}`, { scroll: true })
    }

    const PaginationControls = ({ compact = false }) => {
        if (resolvedScrollMode !== 'pagination' || allPages <= 1) return null
        const getPageNumbers = () => {
            if (allPages <= 7) return Array.from({ length: allPages }, (_, i) => i + 1)
            if (page <= 4)            return [1, 2, 3, 4, 5, '...', allPages]
            if (page >= allPages - 3) return [1, '...', allPages - 4, allPages - 3, allPages - 2, allPages - 1, allPages]
            return [1, '...', page - 1, page, page + 1, '...', allPages]
        }
        const baseBtn = {
            padding: compact ? '4px 8px' : '6px 10px',
            borderRadius: btnRadius, background: '#fff', color: '#374151',
            fontSize: compact ? 12 : 13, cursor: 'pointer',
            border: `1px solid ${pBorderColor}`,
        }
        return (
            <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, paddingTop: compact ? 12 : 32, paddingBottom: compact ? 12 : 32, flexWrap: 'wrap' }}>
                <button disabled={page === 1} onClick={() => goToPage(page - 1)}
                    style={{ ...baseBtn, opacity: page === 1 ? 0.3 : 1 }}>‹</button>
                {getPageNumbers().map((p, i) =>
                    p === '...'
                        ? <span key={`e-${i}`} style={{ padding: '4px', fontSize: 12, color: '#9ca3af' }}>…</span>
                        : <button key={p} onClick={() => goToPage(p)} style={{
                            ...baseBtn,
                            border:     `1px solid ${p === page ? pActiveBg : pBorderColor}`,
                            background: p === page ? pActiveBg : '#fff',
                            color:      p === page ? pActiveTc : '#374151',
                            fontWeight: p === page ? 700 : 400,
                          }}>{p}</button>
                )}
                <button disabled={page === allPages} onClick={() => goToPage(page + 1)}
                    style={{ ...baseBtn, opacity: page === allPages ? 0.3 : 1 }}>›</button>
            </div>
        )
    }

    const LoadMoreButton = ({ compact = false }) => {
        if (resolvedScrollMode !== 'loadmore' || isLoading || page >= allPages) return null
        return (
            <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: compact ? 12 : 32, paddingBottom: compact ? 12 : 32 }}>
                <button onClick={() => setPage(p => p + 1)}
                    style={{ width: compact ? '100%' : 300, maxWidth: 300, padding: '8px 16px', background: btnBg, color: btnTextColor, borderRadius: btnRadius, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                    {dictionary?.property?.loadMore ?? 'Load More'}
                </button>
            </div>
        )
    }

    const newLimit = () => {
        if (resolvedScrollMode === 'infinite' && page < allPages) setPage(p => p + 1)
    }

    // ── View toggle pill — shown in both modes when both views are allowed ──────
    const ViewTogglePill = () => {
        if (!allowListView || !allowMapView) return null
        const activeBg = rv(pActiveBg)
        const activeTc = rv(pActiveTc, '#fff')
        return (
            <div style={{ display: 'inline-flex', background: '#f1f5f9', borderRadius: 8, padding: 3, gap: 2 }}>
                {[
                    { isMap: false, icon: <GridIcon />, label: dictionary?.property?.listView ?? 'List' },
                    { isMap: true,  icon: <MapIcon />,  label: dictionary?.property?.mapView  ?? 'Map'  },
                ].map(item => {
                    const active = showMap === item.isMap
                    return (
                        <button key={String(item.isMap)} onClick={() => handleViewToggle(item.isMap)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 5,
                                padding: '5px 12px', borderRadius: 6, border: 'none',
                                background: active ? activeBg : 'transparent',
                                color:      active ? activeTc : '#6b7280',
                                fontSize: 12, fontWeight: active ? 600 : 400,
                                cursor: 'pointer', transition: 'all 0.15s',
                            }}
                        >
                            {item.icon}{item.label}
                        </button>
                    )
                })}
            </div>
        )
    }

    // ── Map config passthrough ─────────────────────────────────────────────────
    const mapPassthrough = {
        ...mapCfg,
        markerStyle:         mapCfg.marker?.style        ?? 'pin',
        markerColor:         mapCfg.marker?.color        ?? '#3b82f6',
        markerSize:          mapCfg.marker?.size         ?? 28,
        markerSizeMobile:    mapCfg.marker?.sizeMobile   ?? 24,
        markerActiveStyle:   mapCfg.markerActive?.style  ?? 'pin',
        markerActiveColor:   mapCfg.markerActive?.color  ?? '#ef4444',
        markerActiveSize:    mapCfg.markerActive?.size   ?? 36,
        markerActiveSizeMob: mapCfg.markerActive?.sizeMobile ?? 30,
        markerPillBg:        mapCfg.marker?.pillBg           ?? mapCfg.marker?.color ?? '#3b82f6',
        markerPillTextColor: mapCfg.marker?.pillTextColor     ?? '#ffffff',
        markerPillRadius:    mapCfg.marker?.pillRadius        ?? 11,
        markerPillFontSize:  mapCfg.marker?.pillFontSize      ?? 11,
        markerPriceShorten:  mapCfg.marker?.priceShorten      !== false,
    }

    // ── Map view — identical markup for both layout modes ──────────────────────
    const sidebarStyle = {
        flexBasis:   `${splitRatio}%`,
        flexShrink:  0,
        order:       sidebarSide === 'right' ? 2 : 0,
        borderRight: sidebarSide === 'left'  ? '1px solid #e5e7eb' : 'none',
        borderLeft:  sidebarSide === 'right' ? '1px solid #e5e7eb' : 'none',
    }
    const mapPanelStyle = {
        flex: 1, order: sidebarSide === 'right' ? 0 : 2,
    }

    // MapView rendered via stable external component (prevents sidebar remount on hover)

    // ── Result count bar (Rightmove mode) ──────────────────────────────────────
    const ResultCountBar = () => {
        if (!rmShowCount) return null
        return (
            <div style={{
                padding: '8px 12px', fontSize: '0.8rem', fontWeight: 600, color: '#374151',
                borderBottom: `1px solid ${rmBorderColor}`, background: rmListBg, flexShrink: 0,
            }}>
                {propertyCount} {dictionary?.property?.propertiesFound ?? 'properties found'}
            </div>
        )
    }

    const NoResultsMessage = () => (
        <div style={{
            gridColumn: '1 / -1',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '60px 20px',
            color: '#6b7280',
            gap: 8,
        }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#374151' }}>
                {dictionary?.property?.noResultsTitle ?? 'No properties found'}
            </div>
            <div style={{ fontSize: 13, maxWidth: 320 }}>
                {dictionary?.property?.noResultsSubtitle ?? 'Try adjusting your filters or search criteria.'}
            </div>
        </div>
    )

    // ─────────────────────────────────────────────────────────────────────────
    // RIGHTMOVE MODE
    // Fixed-height scrollable panel. Map toggle replaces the card panel with
    // the standard map+sidebar view — exactly the same as standard mode.
    // ─────────────────────────────────────────────────────────────────────────
    if (isRightmove) {
        return (
            <div className='container-wide mx-auto'>

                {/* View toggle — same as standard mode */}
                {/* {allowListView && allowMapView && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: 8 }}>
                        <ViewTogglePill />
                    </div>
                )} */}

                {showMap ? (
                    // Map view — identical to standard mode
                    <MapSidebarView
                        properties={properties}
                        isLoading={isLoading}
                        mapColsDesktop={mapColsDesktop}
                        sidebarStyle={sidebarStyle}
                        mapPanelStyle={mapPanelStyle}
                        swiperRef={swiperRef}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        onSlideChange={handleSlideChange}
                        onMarkerClick={handleMarkerClick}
                        newLimit={newLimit}
                        LoadMoreButton={LoadMoreButton}
                        PaginationControls={PaginationControls}
                        cardStyle={cardStyle}
                        mapCardStyle={mapCardStyle}
                        lang={lang}
                        dictionary={dictionary}
                        searchParams={searchParams}
                        coordinates={coordinates}
                        hoveredPropertyRef={hoveredPropertyRef}
                        activeProperty={activeProperty}
                        activePropertyLat={activePropertyLat}
                        activePropertyLng={activePropertyLng}
                        mapPassthrough={mapPassthrough}
                    />
                ) : (
                    // Rightmove-style fixed-height scrollable list
                    <div style={{
                        width: window.innerWidth >= 1024 ? '70%' : '100%',
                        height: `${rmVh}vh`,
                        display: 'flex',
                        flexDirection: 'column',
                        border: `1px solid ${rmBorderColor}`,
                        background: rmListBg,
                        overflow: 'hidden',
                        borderRadius: rmCfg.borderRadius ?? '8px',
                    }}>
                        {rmCountPos === 'top' && <ResultCountBar />}

                        {/* Scrollable cards */}
                        <div style={{
                            flex: 1, overflowY: 'auto', padding: '12px',
                            display: 'grid',
                            gridTemplateColumns: `repeat(${rmListCols}, 1fr)`,
                            gap: rmCardGap,
                            alignContent: properties.length === 0 ? 'center' : 'start',
                        }}>
                            {properties.length === 0 && !isLoading && hasFetchedOnce ? (
                                <NoResultsMessage />
                            ) : (
                                <>
                                    {properties.map((property, index) => (
                                        <LargePropertyCard
                                            key={property.id}
                                            lang={lang}
                                            saleType={searchParams.saleType}
                                            dictionary={dictionary.card}
                                            property={property}
                                            isLast={index === properties.length - 1}
                                            newLimit={newLimit}
                                            cardStyle={cardStyle}
                                        />
                                    ))}
                                    {isLoading && Array.from({ length: rmListCols * 3 }).map((_, i) => (
                                        <LoadingCard key={`sk-${i}`} cardStyle={cardStyle} />
                                    ))}
                                </>
                            )}
                        </div>

                        <LoadMoreButton compact />
                        <PaginationControls compact />

                        {rmCountPos === 'bottom' && <ResultCountBar />}
                    </div>
                )}
            </div>
        )
    }

    // ─────────────────────────────────────────────────────────────────────────
    // STANDARD MODE (unchanged)
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className='container-wide sm:min-h-[80vh] gap-4 flex md:flex-row flex-col flex-nowrap md:flex-wrap mx-auto lg:px-3 sm:px-0 px-3'>

            {/* {allowListView && allowMapView && (
                <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', paddingBottom: 4 }}>
                    <ViewTogglePill />
                </div>
            )} */}

            {showMap ? (
                <MapSidebarView
                    properties={properties}
                    isLoading={isLoading}
                    mapColsDesktop={mapColsDesktop}
                    sidebarStyle={sidebarStyle}
                    mapPanelStyle={mapPanelStyle}
                    swiperRef={swiperRef}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onSlideChange={handleSlideChange}
                    onMarkerClick={handleMarkerClick}
                    newLimit={newLimit}
                    LoadMoreButton={LoadMoreButton}
                    PaginationControls={PaginationControls}
                    cardStyle={cardStyle}
                    mapCardStyle={mapCardStyle}
                    lang={lang}
                    dictionary={dictionary}
                    searchParams={searchParams}
                    coordinates={coordinates}
                    hoveredPropertyRef={hoveredPropertyRef}
                    activeProperty={activeProperty}
                    activePropertyLat={activePropertyLat}
                    activePropertyLng={activePropertyLng}
                    mapPassthrough={mapPassthrough}
                />
            ) : (
                <>
                    <style>{`
                        #prop-grid {
                            display: grid;
                            gap: ${cardGap}px;
                            grid-template-columns: repeat(${colsMobile}, 1fr);
                            align-items: start;
                        }
                        @media (min-width: 640px) {
                            #prop-grid { grid-template-columns: repeat(${colsTablet}, 1fr); }
                        }
                        @media (min-width: 1024px) {
                            #prop-grid { grid-template-columns: repeat(${colsDesktop}, 1fr); }
                        }
                    `}</style>
                    <div id="prop-grid" style={{ width: '100%' }}>
                        {properties.length === 0 && !isLoading && hasFetchedOnce ? (
                            <NoResultsMessage />
                        ) : (
                            <>
                                {properties.map((property, index) => (
                                    <LargePropertyCard
                                        key={property.id}
                                        lang={lang}
                                        saleType={searchParams.saleType}
                                        dictionary={dictionary.card}
                                        property={property}
                                        isLast={index === properties.length - 1}
                                        newLimit={newLimit}
                                        cardStyle={cardStyle}
                                    />
                                ))}
                                {isLoading && Array.from({ length: colsDesktop }).map((_, i) => (
                                    <LoadingCard key={`sk-${i}`} cardStyle={cardStyle} />
                                ))}
                            </>
                        )}
                    </div>
                    <LoadMoreButton />
                    <PaginationControls />
                </>
            )}
        </div>
    )
}

export default SearchResultCards